import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DryEyeCategory,
  DryEyeSeverity,
  DryEyeType,
  isDryEyeCategoryEqual
} from '../../../../core/remote-json/treatments/dry-eye-category.model';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DryEyeCategoryComponent),
  multi: true
};

@Component({
  selector: 'csi-dry-eye-category',
  templateUrl: './dry-eye-category.component.html',
  styleUrls: ['./dry-eye-category.component.css'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DryEyeCategoryComponent implements ControlValueAccessor, AfterViewInit {
  static readonly allCategories: DryEyeCategory[] = Object.keys(DryEyeSeverity)
    .map(severity =>
      Object.keys(DryEyeType).map(type => {
        return { severity: DryEyeSeverity[severity], type: DryEyeType[type] };
      })
    )
    .reduce((flattenedAray, dryEyeTypeArray) => flattenedAray.concat(dryEyeTypeArray));

  @Input() preOpScreening: boolean;
  @Input() predicted: string | null = null;

  disabled = false;
  svgTransform = 'scale(-1,1) translate(-100,0)';

  private _selectedCategory: DryEyeCategory;
  private _onChange: (_: any) => {};
  private _onTouched = () => {};

  constructor(private location: Location, private changeDetectorRef: ChangeDetectorRef) {}

  ngAfterViewInit() {
    setTimeout(() => {
      this.svgTransform = ` ${this.svgTransform}`;
    }, 750);
  }

  set selectedCategory(category: DryEyeCategory) {
    this._selectedCategory = category === this._selectedCategory ? null : category;
    this._onChange(this._selectedCategory);
  }

  get selectedCategory(): DryEyeCategory {
    return this._selectedCategory;
  }

  get allCategories(): DryEyeCategory[] {
    return DryEyeCategoryComponent.allCategories;
  }

  writeValue(value: DryEyeCategory) {
    if (!!value && !this.preOpScreening) {
      this._selectedCategory = DryEyeCategoryComponent.allCategories.find(category =>
        isDryEyeCategoryEqual(category, value)
      );
    } else {
      this._selectedCategory = value;
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  get svgFillStyle(): string {
    return `fill: url('${this.location.path()}#scaleGradient');`;
  }

  isPredicted(category: DryEyeCategory | string): boolean {
    if (!this.predicted) return false;
    const { sev, type } = this.parsePrediction(this.predicted);
    if (this.preOpScreening) {
      if (!sev || typeof category !== 'string') return false;
      return this.norm(category) === this.norm(sev);
    }
    if (!sev || !type || typeof category === 'string') return false;
    const sevEnum = this.toEnumSeverity(sev);
    const typeEnum = this.toEnumType(type);
    if (sevEnum === undefined || typeEnum === undefined) return false;
    const predictedCat: DryEyeCategory = { severity: sevEnum, type: typeEnum };
    return isDryEyeCategoryEqual(category, predictedCat);
  }

  private norm(s: string) {
    return (s || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  private parsePrediction(
    pred: string | null
  ): {
    sev?: 'mild' | 'moderate' | 'severe';
    type?: 'aqueous' | 'mixed' | 'evaporative';
  } {
    const p = this.norm(pred || '');
    const sev = (['mild', 'moderate', 'severe'] as const).find(s => p.startsWith(s));
    const type = p.includes('aqueous')
      ? 'aqueous'
      : p.includes('evaporative')
      ? 'evaporative'
      : p.includes('mixed')
      ? 'mixed'
      : undefined;
    return { sev, type };
  }

  private toEnumSeverity(k?: 'mild' | 'moderate' | 'severe'): DryEyeSeverity | undefined {
    if (!k) return undefined;
    if (k === 'mild') return DryEyeSeverity.Mild;
    if (k === 'moderate') return DryEyeSeverity.Moderate;
    if (k === 'severe') return DryEyeSeverity.Severe;
    return undefined;
  }

  private toEnumType(k?: 'aqueous' | 'mixed' | 'evaporative'): DryEyeType | undefined {
    if (!k) return undefined;
    if (k === 'aqueous') return DryEyeType.Aqueous;
    if (k === 'mixed') return DryEyeType.Mixed;
    if (k === 'evaporative') return DryEyeType.Evaporative;
    return undefined;
  }

  onRainbowMove(e: MouseEvent) {
    const el = e.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
    el.style.setProperty('--angle', `${deg}deg`);
  }

  onRainbowLeave(e: MouseEvent) {
    const el = e.currentTarget as HTMLElement;
    el.style.removeProperty('--angle');
  }
}
